import React, { useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import AppSidebar from '../components/AppSidebar';
import Navbar from '../components/Navbar';
import ProtectedRoute from '../components/ProtectedRoute';
import { useAuth } from '../context/AuthContext';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import HomeDashboard from './HomeDashboard';
import IeemDashboard from './IeemDashboard';
import ProductDashboard from './ProductDashboard';
import ManageUsers from './ManageUsers';
import PackageManager from './PackageManager';
import Airports from './Airports';
import AviationServices from './AviationServices';
import Hospitality from './Hospitality';
import IntegrationData from './IntegrationData';
import DestinationManagement from './DestinationManagement';
import Retail from './Retail';
import RetailIntegrationData from './RetailIntegrationData';
import TourismDevelopment from './TourismDevelopment';
import UserActivityLog from './UserActivityLog';
import ManageMenuCategory from './ManageMenuCategory';
import ManageMenuSubCategory from './ManageMenuSubCategory';
import ManageMenu from './ManageMenu';
import ProductMonitoring from './ProductMonitoring';
import { ProductMonitoringDetailPage } from '@/components/product-monitoring/ProductDetailExperience';
import PurchaseSimulator from './PurchaseSimulator';
import ApiDocumentation from './ApiDocumentation';
import MemberIntegration from './MemberIntegration';
import FtpHotelMapping from './FtpHotelMapping';
import HinHotelMaster from './HinHotelMaster';
import ChannelIntegrationControl from './ChannelIntegrationControl';

const AdminDashboard = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const isInventoryRoute =
      location.pathname.includes('/admin/airports') ||
      location.pathname.includes('/admin/aviation-services') ||
      location.pathname.includes('/admin/hospitality') ||
      location.pathname.includes('/admin/destination-management') ||
      location.pathname.includes('/admin/retail') ||
      location.pathname.includes('/admin/tourism-development');

    if (!isInventoryRoute) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    toast.success('Anda telah keluar dari sistem', { description: 'Logout berhasil' });
    navigate('/login');
  };

  const [sidebarOpen, setSidebarOpen] = React.useState(() => {
    const raw = localStorage.getItem('sidebarMinimized');
    if (raw === null) return true;
    return !JSON.parse(raw);
  });

  const handleSidebarOpenChange = React.useCallback((open) => {
    setSidebarOpen(open);
    localStorage.setItem('sidebarMinimized', JSON.stringify(!open));
    window.dispatchEvent(new Event('sidebarToggle'));
  }, []);

  React.useEffect(() => {
    const handleSidebarToggle = () => {
      const saved = localStorage.getItem('sidebarMinimized');
      const collapsed = saved !== null ? JSON.parse(saved) : false;
      setSidebarOpen(!collapsed);
    };
    window.addEventListener('sidebarToggle', handleSidebarToggle);
    return () => {
      window.removeEventListener('sidebarToggle', handleSidebarToggle);
    };
  }, []);

  return (
    <SidebarProvider
      open={sidebarOpen}
      onOpenChange={handleSidebarOpenChange}
      className="min-h-screen"
      style={{
        '--sidebar-width': '17.5rem',
        '--sidebar-width-icon': '4rem',
      }}
    >
      <AppSidebar onLogout={handleLogout} />
      <SidebarInset className="min-h-screen bg-[#F4F7FE]">
        <Navbar />
        <div className="min-h-[calc(100vh-70px)] p-4 lg:p-6">
          <Routes>
              <Route path="/" element={<ProtectedRoute><HomeDashboard /></ProtectedRoute>} />
              <Route path="/ieem-dashboard" element={<ProtectedRoute><IeemDashboard /></ProtectedRoute>} />
              <Route path="/product-dashboard" element={<ProtectedRoute><ProductDashboard /></ProtectedRoute>} />
              <Route path="/manage-users" element={<ProtectedRoute><ManageUsers /></ProtectedRoute>} />
              <Route path="/internal-tools/purchase-simulator" element={<ProtectedRoute><PurchaseSimulator /></ProtectedRoute>} />
              <Route path="/internal-tools/api-documentation" element={<ProtectedRoute><ApiDocumentation /></ProtectedRoute>} />
              <Route path="/internal-tools/api-documentation/:memberCode" element={<ProtectedRoute><ApiDocumentation /></ProtectedRoute>} />
              <Route path="/internal-tools/member-integration" element={<ProtectedRoute><MemberIntegration /></ProtectedRoute>} />
              <Route path="/internal-tools/member-integration/:memberCode" element={<ProtectedRoute><MemberIntegration /></ProtectedRoute>} />
              <Route path="/internal-tools/ftp-hotel-mapping" element={<ProtectedRoute><FtpHotelMapping /></ProtectedRoute>} />
              <Route path="/internal-tools/integration-settings" element={<ProtectedRoute><ChannelIntegrationControl /></ProtectedRoute>} />

              <Route path="/package-manager" element={<Navigate to="/admin/package-manager/overview" replace />} />
              <Route path="/package-manager/*" element={<ProtectedRoute><PackageManager /></ProtectedRoute>} />
              {/* Redirect from legacy service-monitoring paths */}
              <Route path="/service-monitoring/packages" element={<Navigate to="/admin/product-monitoring/products" replace />} />
              <Route path="/service-monitoring/tickets" element={<Navigate to="/admin/product-monitoring/products" replace />} />

              {/* Product Monitoring */}
              <Route path="/product-monitoring" element={<Navigate to="/admin/product-monitoring/products" replace />} />
              <Route path="/product-monitoring/overview" element={<ProtectedRoute><ProductMonitoring /></ProtectedRoute>} />
              <Route path="/product-monitoring/products" element={<ProtectedRoute><ProductMonitoring /></ProtectedRoute>} />
              <Route path="/product-monitoring/products/:rowId" element={<ProtectedRoute><ProductMonitoringDetailPage /></ProtectedRoute>} />
              <Route path="/product-monitoring/reservations" element={<ProtectedRoute><ProductMonitoring /></ProtectedRoute>} />
              <Route path="/product-monitoring/channels" element={<ProtectedRoute><ProductMonitoring /></ProtectedRoute>} />
              <Route path="/product-monitoring/members" element={<ProtectedRoute><ProductMonitoring /></ProtectedRoute>} />
              {/* Legacy category routes remain valid but now resolve to a filter dimension. */}
              <Route path="/product-monitoring/packages" element={<Navigate to="/admin/product-monitoring/products?type=package" replace />} />
              <Route path="/product-monitoring/airports" element={<Navigate to="/admin/product-monitoring/products?member=api" replace />} />
              <Route path="/product-monitoring/aviation-services" element={<Navigate to="/admin/product-monitoring/products?member=ias" replace />} />
              <Route path="/product-monitoring/hospitality" element={<Navigate to="/admin/product-monitoring/products?member=hin" replace />} />
              <Route path="/product-monitoring/destination-management" element={<Navigate to="/admin/product-monitoring/products?member=twc" replace />} />
              <Route path="/product-monitoring/retail" element={<Navigate to="/admin/product-monitoring/products?member=retail" replace />} />
              <Route path="/product-monitoring/tourism-development" element={<Navigate to="/admin/product-monitoring/products?member=itdc" replace />} />
              <Route path="/my-bundling-package" element={<Navigate to="/admin/package-manager/my-packages" replace />} />
              <Route path="/browse-inventory" element={<Navigate to="/admin/package-manager/product-catalog" replace />} />
              <Route path="/dashboard-ieem" element={<Navigate to="/admin/ieem-dashboard" replace />} />

              <Route path="/airports" element={<Navigate to="/admin/airports/ieem" replace />} />
              <Route path="/airports/ieem" element={<ProtectedRoute><Airports /></ProtectedRoute>} />
              <Route path="/airports/bundling-product" element={<ProtectedRoute><Airports /></ProtectedRoute>} />

              <Route path="/aviation-services" element={<Navigate to="/admin/aviation-services/ieem" replace />} />
              <Route path="/aviation-services/ieem" element={<ProtectedRoute><AviationServices /></ProtectedRoute>} />
              <Route path="/aviation-services/bundling-product" element={<ProtectedRoute><AviationServices /></ProtectedRoute>} />
              <Route path="/aviation-services/integrationData" element={<ProtectedRoute><AviationServices /></ProtectedRoute>} />

              <Route path="/hospitality" element={<Navigate to="/admin/hospitality/ieem" replace />} />
              <Route path="/hospitality/ieem" element={<ProtectedRoute><Hospitality /></ProtectedRoute>} />
              <Route path="/hospitality/hotel-master" element={<ProtectedRoute><HinHotelMaster /></ProtectedRoute>} />
              <Route path="/hospitality/availability" element={<ProtectedRoute><HinHotelMaster /></ProtectedRoute>} />
              <Route path="/hospitality/bundling-product" element={<ProtectedRoute><Hospitality /></ProtectedRoute>} />
              <Route path="/hospitality/integrationData" element={<ProtectedRoute><IntegrationData /></ProtectedRoute>} />

              <Route path="/destination-management" element={<Navigate to="/admin/destination-management/ieem" replace />} />
              <Route path="/destination-management/ieem" element={<ProtectedRoute><DestinationManagement /></ProtectedRoute>} />
              <Route path="/destination-management/bundling-product" element={<ProtectedRoute><DestinationManagement /></ProtectedRoute>} />
              <Route path="/destination-management/integrationData" element={<ProtectedRoute><DestinationManagement /></ProtectedRoute>} />
              <Route path="/destination-management/goers-content" element={<ProtectedRoute><DestinationManagement /></ProtectedRoute>} />

              <Route path="/retail" element={<Navigate to="/admin/retail/ieem" replace />} />
              <Route path="/retail/ieem" element={<ProtectedRoute><Retail /></ProtectedRoute>} />
              <Route path="/retail/bundling-product" element={<ProtectedRoute><Retail /></ProtectedRoute>} />
              <Route path="/retail/sarinah-coupons" element={<ProtectedRoute><Retail /></ProtectedRoute>} />
              <Route path="/retail/integrationData" element={<ProtectedRoute><RetailIntegrationData /></ProtectedRoute>} />

              <Route path="/tourism-development" element={<Navigate to="/admin/tourism-development/ieem" replace />} />
              <Route path="/tourism-development/ieem" element={<ProtectedRoute><TourismDevelopment /></ProtectedRoute>} />
              <Route path="/tourism-development/bundling-product" element={<ProtectedRoute><TourismDevelopment /></ProtectedRoute>} />
              <Route path="/tourism-development/goers-content" element={<ProtectedRoute><TourismDevelopment /></ProtectedRoute>} />

              <Route path="/master-data" element={<Navigate to="/admin/master-data/menu" replace />} />
              <Route path="/master-data/menu" element={<ProtectedRoute><ManageMenu /></ProtectedRoute>} />
              <Route path="/master-data/menu-category" element={<ProtectedRoute><ManageMenuCategory /></ProtectedRoute>} />
              <Route path="/master-data/menu-subcategory" element={<ProtectedRoute><ManageMenuSubCategory /></ProtectedRoute>} />
              <Route path="/master-data/user-activity-log" element={<ProtectedRoute><UserActivityLog /></ProtectedRoute>} />
          </Routes>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
};

export default AdminDashboard;
